import type { SearchParams } from '@app/constants/StoreTypes'

import { AddressManager } from '@app/utils/AddressManager'
import { createCookie } from '@app/utils/cookies'
import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'
import { tryJSONparse } from '@app/utils/tryJSONparse'
import { valita } from '@app/utils/valita'

import { createAddress } from '@app/packages/geo/Address'
import { isValidLatitude } from '@app/packages/geo/Point'
import { unwrapOr } from '@app/packages/Result/Result'

import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

const searchParamsSlice = createReduxSlice<SearchParams>('search_params')

export const setSearchParams = searchParamsSlice.set

export const rawSearchParamsSelector = createSelector([searchParamsSlice.selector], params => params ?? EMPTY_PARAMS)

export function restoreSearchParams() {
  return createThunk(async (dispatch, getState) => {
    const params = await searchCookie.get(dispatch(getCookies()))
    dispatch(setSearchParams(params))
    return rawSearchParamsSelector(getState())
  })
}

export const searchCookie = createCookie<SearchParams>(
  'kidsout__sitter-search',
  v => {
    const data = unwrapOr(tryJSONparse<SearchParams>(v), {})
    // Handle old style data (e.g {address: "Moscow", latitude: 57, longitude: 36})
    const addressV1 = RT_SearchAddressStateV1.try(data)
    if (addressV1.ok) {
      data.address = createAddress({
        label: addressV1.value.address,
        location: { lat: addressV1.value.latitude, lon: addressV1.value.longitude },
      })
    } else if (data.address) {
      const legacy = RT_LegacyAddress.try(data.address)
      if (legacy.ok) {
        data.address = AddressManager.fromLegacyJSON(legacy.value).address
      } else {
        const address = RT_Address.try(data.address)
        if (address.ok) {
          data.address = address.value
        } else {
          delete data.address
        }
      }
    }
    return data
  },
  v => JSON.stringify(v),
  () => ({ expires: moment().add(1, 'year'), sameSite: 'lax' })
)

const EMPTY_PARAMS: SearchParams = {}

const RT_Latitude = valita.number().assert(isValidLatitude)

const RT_Coord = valita.tuple([RT_Latitude, valita.number()])

const RT_LegacyAddress = valita.object({
  latitude: RT_Latitude,
  longitude: valita.number(),
  address: valita
    .string()
    .map(v => v.trim())
    .assert(v => v.length >= 1)
    .optional(),
  kind: valita
    .string()
    .assert(v => v.length >= 1)
    .optional(),
  lowerCorner: RT_Coord.optional(),
  upperCorner: RT_Coord.optional(),
})

const RT_Address = valita.object({
  label: valita.string().optional(),
  kind: valita.string().optional(),
  location: valita.object({
    lat: RT_Latitude,
    lon: valita.number(),
  }),
  bounds: valita
    .object({
      bottomLeft: valita.object({
        lat: RT_Latitude,
        lon: valita.number(),
      }),
      topRight: valita.object({
        lat: RT_Latitude,
        lon: valita.number(),
      }),
    })
    .optional(),
})

const RT_SearchAddressStateV1 = valita.object({
  latitude: RT_Latitude,
  longitude: valita.number(),
  address: valita
    .string()
    .map(v => v.trim())
    .assert(v => v.length >= 1),
})
